// The project page's Reports tab (design §7): every task in the project
// with a report — title, version, last update, who made it.

import { Link } from '@tanstack/react-router'
import { EmptyState, ErrorNote, LoadingRows, Panel } from '../ui'
import { sceneClass } from '../goblin-art'
import { useProjectReports } from '../../hooks/useReports'
import { formatDateTime, formatRelative } from '../../lib/format'
import { isAgentAuthored } from './report-types'

export function ProjectReportsTab({ projectId }: { projectId: string }) {
  const query = useProjectReports(projectId)
  const reports = query.data?.reports ?? []

  return (
    <Panel
      title="Reports"
      description="Reports agents and people wrote for this project’s tasks, latest change first."
      flush
      className={sceneClass('archive')}
    >
      {query.isLoading ? (
        <LoadingRows label="Loading reports…" />
      ) : query.isError ? (
        <ErrorNote error={query.error} what="reports" />
      ) : reports.length === 0 ? (
        <EmptyState title="No reports yet" art="writing">
          When an agent writes a report for a task, it shows up here.
        </EmptyState>
      ) : (
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Report</th>
                <th>Task</th>
                <th>Version</th>
                <th>Updated</th>
                <th className="w-px" />
              </tr>
            </thead>
            <tbody>
              {reports.map((entry) => (
                <tr key={entry.reportId}>
                  <td>
                    <Link
                      to="/tasks"
                      search={{ taskId: entry.taskId, tab: 'report' }}
                      className="link block font-medium"
                    >
                      {entry.title}
                    </Link>
                    {entry.summary ? (
                      <span className="block text-sm text-ink-3">
                        {entry.summary}
                      </span>
                    ) : null}
                  </td>
                  <td className="text-ink-2">{entry.taskTitle}</td>
                  <td>
                    v{entry.currentVersion}
                    {entry.submissionCount > 0 ? (
                      <span className="block text-sm text-ink-3">
                        {entry.submissionCount} submission
                        {entry.submissionCount === 1 ? '' : 's'}
                      </span>
                    ) : null}
                  </td>
                  <td>
                    <span title={formatDateTime(entry.updatedAt)}>
                      {formatRelative(entry.updatedAt)}
                    </span>
                    <span className="block text-sm text-ink-3">
                      {isAgentAuthored(entry.provenance)
                        ? (entry.provenance.agentName ?? 'Agent')
                        : (entry.provenance.userName ?? 'Someone')}
                    </span>
                  </td>
                  <td className="text-right">
                    <Link
                      to="/tasks/$taskId/report"
                      params={{ taskId: entry.taskId }}
                      className="btn-ghost btn-sm"
                    >
                      Full screen
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  )
}
